import { Card, Col, Row, Statistic, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useQuery } from '@tanstack/react-query';
import { Loading } from '@mall/shared';
import {
  fetchDashboardSummary,
  type BrandProductCountRow,
  type CategoryProductCountRow,
  type OrderLineStatsByBrandRow,
  type OrderLineStatsByCategoryRow,
} from './api';
import { useAuth } from '../auth/AuthProvider';

const PRICE = (cents: number) => `¥ ${(cents / 100).toFixed(2)}`;

const EMPTY_SUMMARY = {
  productCount: 0,
  orderCount: 0,
  pendingOrders: 0,
  completedOrders: 0,
  cancelledOrders: 0,
  totalRevenueCents: 0,
  productsByBrand: [] as BrandProductCountRow[],
  productsByCategory: [] as CategoryProductCountRow[],
  ordersByBrand: [] as OrderLineStatsByBrandRow[],
  ordersByCategory: [] as OrderLineStatsByCategoryRow[],
};

const brandProductColumns: ColumnsType<BrandProductCountRow> = [
  { title: '品牌', dataIndex: 'brandName', key: 'brandName', ellipsis: true },
  { title: '商品数', dataIndex: 'productCount', key: 'productCount', width: 100, align: 'right' },
];

const categoryProductColumns: ColumnsType<CategoryProductCountRow> = [
  { title: '分类', dataIndex: 'categoryName', key: 'categoryName', ellipsis: true },
  { title: '商品数', dataIndex: 'productCount', key: 'productCount', width: 100, align: 'right' },
];

const brandOrderColumns: ColumnsType<OrderLineStatsByBrandRow> = [
  { title: '品牌', dataIndex: 'brandName', key: 'brandName', ellipsis: true },
  { title: '订单数', dataIndex: 'orderCount', key: 'orderCount', width: 90, align: 'right' },
  {
    title: '行小计（全部状态）',
    dataIndex: 'lineTotalCents',
    key: 'lineTotalCents',
    width: 150,
    align: 'right',
    render: (c: number) => PRICE(c),
  },
  {
    title: '行小计（已完成）',
    dataIndex: 'completedLineTotalCents',
    key: 'completedLineTotalCents',
    width: 150,
    align: 'right',
    render: (c: number) => PRICE(c),
  },
];

const categoryOrderColumns: ColumnsType<OrderLineStatsByCategoryRow> = [
  { title: '分类', dataIndex: 'categoryName', key: 'categoryName', ellipsis: true },
  { title: '订单数', dataIndex: 'orderCount', key: 'orderCount', width: 90, align: 'right' },
  {
    title: '行小计（全部状态）',
    dataIndex: 'lineTotalCents',
    key: 'lineTotalCents',
    width: 150,
    align: 'right',
    render: (c: number) => PRICE(c),
  },
  {
    title: '行小计（已完成）',
    dataIndex: 'completedLineTotalCents',
    key: 'completedLineTotalCents',
    width: 150,
    align: 'right',
    render: (c: number) => PRICE(c),
  },
];

export function DashboardPage() {
  const { session } = useAuth();
  const query = useQuery({
    queryKey: ['admin', 'dashboard', 'summary'],
    queryFn: fetchDashboardSummary,
  });

  if (query.isLoading) return <Loading />;

  const data = query.data ?? EMPTY_SUMMARY;

  return (
    <div className="mall-page">
      <div className="mall-page-header">
        <div>
          <div className="mall-page-kicker">Dashboard</div>
          <Typography.Title level={3} className="mall-page-title">
            欢迎回来，{session?.username}
          </Typography.Title>
          <p className="mall-page-description">
            当前租户 #{session?.tenantId} 的经营数据、商品结构和订单维度汇总。
          </p>
        </div>
      </div>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card className="mall-metric-card">
            <Statistic title="商品总数" value={data.productCount} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card className="mall-metric-card">
            <Statistic title="订单总数" value={data.orderCount} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card className="mall-metric-card">
            <Statistic
              title="待处理订单"
              value={data.pendingOrders}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card className="mall-metric-card">
            <Statistic
              title="已完成"
              value={data.completedOrders}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card className="mall-metric-card">
            <Statistic
              title="已取消"
              value={data.cancelledOrders}
              valueStyle={{ color: '#bfbfbf' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card className="mall-metric-card">
            <Statistic
              title="累计销售额"
              value={PRICE(data.totalRevenueCents)}
              valueStyle={{ color: 'var(--mall-color-primary)' }}
            />
          </Card>
        </Col>
      </Row>

      <div>
        <Typography.Title level={5} style={{ marginTop: 0 }}>
          商品统计（按维度）
        </Typography.Title>
        <Typography.Paragraph type="secondary" style={{ marginTop: -4 }}>
          帮助快速识别品牌与分类下的商品分布。
        </Typography.Paragraph>
      </div>
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="商品数 · 按品牌" size="small" className="mall-table-card">
            <Table<BrandProductCountRow>
              size="small"
              rowKey={(r) => String(r.brandId ?? 'none')}
              pagination={false}
              columns={brandProductColumns}
              dataSource={data.productsByBrand}
              locale={{ emptyText: '暂无数据' }}
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="商品数 · 按分类" size="small" className="mall-table-card">
            <Table<CategoryProductCountRow>
              size="small"
              rowKey={(r) => String(r.categoryId ?? 'none')}
              pagination={false}
              columns={categoryProductColumns}
              dataSource={data.productsByCategory}
              locale={{ emptyText: '暂无数据' }}
            />
          </Card>
        </Col>
      </Row>

      <div>
        <Typography.Title level={5} style={{ marginTop: 0 }}>
          订单与行金额（按维度）
        </Typography.Title>
        <Typography.Paragraph type="secondary" style={{ marginTop: -4 }}>
          订单数按「至少包含一条该维度下商品明细」的去重订单计；金额为订单行小计之和（与整单状态无关），「已完成」列为父订单状态为已完成时的行金额合计。
        </Typography.Paragraph>
      </div>
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="订单 · 按品牌" size="small" className="mall-table-card">
            <Table<OrderLineStatsByBrandRow>
              size="small"
              rowKey={(r) => String(r.brandId ?? 'none')}
              pagination={false}
              scroll={{ x: 'max-content' }}
              columns={brandOrderColumns}
              dataSource={data.ordersByBrand}
              locale={{ emptyText: '暂无数据' }}
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="订单 · 按分类" size="small" className="mall-table-card">
            <Table<OrderLineStatsByCategoryRow>
              size="small"
              rowKey={(r) => String(r.categoryId ?? 'none')}
              pagination={false}
              scroll={{ x: 'max-content' }}
              columns={categoryOrderColumns}
              dataSource={data.ordersByCategory}
              locale={{ emptyText: '暂无数据' }}
            />
          </Card>
        </Col>
      </Row>

      {query.isError && (
        <Typography.Paragraph type="warning" style={{ marginTop: 16 }}>
          指标接口调用失败（{query.error instanceof Error ? query.error.message : '未知错误'}），
          已用 0 占位。
        </Typography.Paragraph>
      )}
    </div>
  );
}
